import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utcPlugin from 'dayjs/plugin/utc'
import FlexRow from '../../../../components/molecules/FlexRow'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import FastAvatar from '../../../../components/molecules/FastAvatar'
import { useClient } from '../../../../api/clients'
import MeetingStatusBadge from './MeetingStatusBadge'

dayjs.extend(relativeTime)
dayjs.extend(utcPlugin)

const useStyles = makeStyles(() => ({
  card: {
    display: 'block',
    textDecoration: 'none !important',
    color: 'inherit',
    padding: 'var(--padding-md) var(--padding-lg)',
    border: 'var(--border-neutral-thin)',
    borderColor: 'var(--dai-color-border1)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'var(--dai-bg-card)',
    transition: 'background-color var(--transition-duration-medium) var(--transition-easing-normal)',
    '&:hover': {
      backgroundColor: 'var(--dai-color-hover)'
    }
  }
}))

const PLATFORM_LABELS = {
  teams: 'Teams',
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  webex: 'Webex',
  unknown: 'Meeting'
}

function MeetingCard ({ meeting }) {
  const classes = useStyles()
  const platformLabel = PLATFORM_LABELS[meeting.platform] || PLATFORM_LABELS.unknown
  const { data: client } = useClient(meeting.clientId, { enabled: !!meeting.clientId })

  const timeDisplay = useMemo(() => {
    const t = meeting.startAt || meeting.joinAt
    if (t) {
      const d = dayjs.utc(t).local()
      return d.isAfter(dayjs()) ? d.fromNow() : d.format('MMM D, YYYY h:mm A')
    }
    return dayjs.utc(meeting.createdDate).local().format('MMM D, YYYY h:mm A')
  }, [meeting.startAt, meeting.joinAt, meeting.createdDate])

  return (
    <Link to={`/denali-ai/meetings/${meeting.meetingId}`} className={classes.card}>
      <FlexRow className='justify-between'>
        <FlexColumn className='gap-0' style={{ gap: 2 }}>
          <FlexRow className='gap-sm'>
            <span className='font-size-md font-weight-bold'>
              {meeting.title || 'Untitled Meeting'}
            </span>
            <MeetingStatusBadge status={meeting.status} />
          </FlexRow>
          <FlexRow className='gap-sm font-size-sm' style={{ color: 'var(--dai-color-text-dim)' }}>
            {client && (
              <>
                <FlexRow className='gap-xs' style={{ gap: 4 }}>
                  <FastAvatar avatarUrl={client.avatarUrl} abbreviation={client.clientAbbreviation} size='xxs' />
                  <span>{client.shortName}</span>
                </FlexRow>
                <span>&middot;</span>
              </>
            )}
            <span>{platformLabel}</span>
            <span>&middot;</span>
            <span>{timeDisplay}</span>
          </FlexRow>
        </FlexColumn>
      </FlexRow>
    </Link>
  )
}

MeetingCard.propTypes = {
  meeting: PropTypes.shape({
    meetingId: PropTypes.string.isRequired,
    title: PropTypes.string,
    platform: PropTypes.string,
    status: PropTypes.string.isRequired,
    startAt: PropTypes.string,
    joinAt: PropTypes.string,
    createdDate: PropTypes.string,
    clientId: PropTypes.number
  }).isRequired
}

export default React.memo(MeetingCard)
